import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, CheckCircle2, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VENEZUELAN_STATES = [
  'Amazonas', 'Anzoategui', 'Apure', 'Aragua', 'Barinas', 'Bolivar', 'Carabobo',
  'Cojedes', 'Delta Amacuro', 'Distrito Capital', 'Falcon', 'Guarico', 'Lara',
  'Merida', 'Miranda', 'Monagas', 'Nueva Esparta', 'Portuguesa', 'Sucre',
  'Tachira', 'Trujillo', 'Vargas', 'Yaracuy', 'Zulia'
];

const CITIES = [
  'Caracas', 'Maracaibo', 'Valencia', 'Barquisimeto', 'Merida', 'Maracay',
  'Puerto La Cruz', 'Ciudad Guayana', 'San Cristobal', 'Margarita', 'Porlamar',
  'Cumana', 'Los Teques', 'Guarenas', 'Guatire', 'Barinas', 'Punto Fijo'
];

export default function ProfileCompletion() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const t = (en, es) => language === 'es' ? es : en;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/users/profile`, { headers }).then(r => {
      setProfile(r.data);
      setForm(r.data);
    }).catch(() => {});
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.completion;
      if (payload.budget_min) payload.budget_min = parseFloat(payload.budget_min);
      if (payload.budget_max) payload.budget_max = parseFloat(payload.budget_max);
      await axios.put(`${API}/users/profile`, payload, { headers });
      const res = await axios.get(`${API}/users/profile`, { headers });
      setProfile(res.data);
      setForm(res.data);
      toast.success(t('Profile updated!', 'Perfil actualizado!'));
    } catch {
      toast.error(t('Failed to update profile', 'Error al actualizar perfil'));
    }
    setSaving(false);
  };

  if (!profile) return null;
  const pct = profile.completion;

  return (
    <Card className="mb-6 overflow-hidden" data-testid="profile-completion-card">
      <CardContent className="p-0">
        {/* Header bar */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
          data-testid="profile-toggle-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">
                {t('Complete Your Profile', 'Completa Tu Perfil')}
              </p>
              <p className="text-xs text-slate-500">
                {pct === 100
                  ? t('Profile complete!', 'Perfil completo!')
                  : t(`${pct}% complete — help us find better matches`, `${pct}% completado — te ayudamos a encontrar mejores opciones`)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pct === 100 ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <Badge className="bg-[#D4AF37] text-white text-xs">{pct}%</Badge>
            )}
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </button>

        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} data-testid="profile-progress-bar" />
        </div>

        {/* Expandable form */}
        {expanded && (
          <div className="px-5 py-5 border-t border-border space-y-5 animate-fade-in" data-testid="profile-form">
            {/* Row 1: Intent + Experience */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('I am a...', 'Soy...')}</Label>
                <Select value={form.intent || ''} onValueChange={v => setForm(f => ({ ...f, intent: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-intent"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">{t('Buyer', 'Comprador')}</SelectItem>
                    <SelectItem value="renter">{t('Renter', 'Inquilino')}</SelectItem>
                    <SelectItem value="seller">{t('Seller', 'Vendedor')}</SelectItem>
                    <SelectItem value="investor">{t('Investor', 'Inversionista')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('Experience', 'Experiencia')}</Label>
                <Select value={form.experience || ''} onValueChange={v => setForm(f => ({ ...f, experience: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-experience"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_time">{t('First Time', 'Primera Vez')}</SelectItem>
                    <SelectItem value="experienced">{t('Experienced', 'Con Experiencia')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('Timeline', 'Plazo')}</Label>
                <Select value={form.timeline || ''} onValueChange={v => setForm(f => ({ ...f, timeline: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-timeline"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">{t('Immediate', 'Inmediato')}</SelectItem>
                    <SelectItem value="3-6 months">{t('3-6 Months', '3-6 Meses')}</SelectItem>
                    <SelectItem value="browsing">{t('Just Browsing', 'Solo Mirando')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Property Type + Budget */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('Property Type', 'Tipo de Propiedad')}</Label>
                <Select value={form.preferred_property_type || ''} onValueChange={v => setForm(f => ({ ...f, preferred_property_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-property-type"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="house">{t('House', 'Casa')}</SelectItem>
                    <SelectItem value="apartment">{t('Apartment', 'Apartamento')}</SelectItem>
                    <SelectItem value="land">{t('Land', 'Terreno')}</SelectItem>
                    <SelectItem value="acreage">{t('Acreage', 'Finca')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('Min Budget ($)', 'Presupuesto Min ($)')}</Label>
                <Input type="number" className="mt-1" value={form.budget_min || ''} data-testid="profile-budget-min"
                  onChange={e => setForm(f => ({ ...f, budget_min: e.target.value }))} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('Max Budget ($)', 'Presupuesto Max ($)')}</Label>
                <Input type="number" className="mt-1" value={form.budget_max || ''} data-testid="profile-budget-max"
                  onChange={e => setForm(f => ({ ...f, budget_max: e.target.value }))} placeholder="500,000" />
              </div>
            </div>

            {/* Row 3: Location */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('Country', 'Pais')}</Label>
                <Select value={form.country || ''} onValueChange={v => setForm(f => ({ ...f, country: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-country"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {["Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
                      "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
                      "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
                      "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica",
                      "Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","East Timor","Ecuador",
                      "Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France",
                      "Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau",
                      "Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland",
                      "Israel","Italy","Ivory Coast","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kosovo",
                      "Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania",
                      "Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius",
                      "Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia",
                      "Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway",
                      "Oman","Pakistan","Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland",
                      "Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines",
                      "Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore",
                      "Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan",
                      "Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Togo","Tonga",
                      "Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates",
                      "United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
                    ].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('State / Region', 'Estado / Region')}</Label>
                <Select value={form.state_region || ''} onValueChange={v => setForm(f => ({ ...f, state_region: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-state"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent>
                    {VENEZUELAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('City / Suburb', 'Ciudad')}</Label>
                <Select value={form.city_suburb || ''} onValueChange={v => setForm(f => ({ ...f, city_suburb: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-city"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent>
                    {CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 4: Age Range + Save */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-xs font-medium text-slate-600">{t('Age Range', 'Rango de Edad')}</Label>
                <Select value={form.age_range || ''} onValueChange={v => setForm(f => ({ ...f, age_range: v }))}>
                  <SelectTrigger className="mt-1" data-testid="profile-age"><SelectValue placeholder={t('Select', 'Seleccionar')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="18-24">18-24</SelectItem>
                    <SelectItem value="25-34">25-34</SelectItem>
                    <SelectItem value="35-44">35-44</SelectItem>
                    <SelectItem value="45-54">45-54</SelectItem>
                    <SelectItem value="55-64">55-64</SelectItem>
                    <SelectItem value="65+">65+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button onClick={handleSave} disabled={saving} className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] px-8" data-testid="profile-save-btn">
                  {saving ? t('Saving...', 'Guardando...') : t('Save Profile', 'Guardar Perfil')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
